// src/pages/Presentation/SmartVideoPlayerGallery.tsx
import React, {
  useRef,
  useEffect,
  useState,
  memo,
  CSSProperties,
  useMemo,
} from 'react';
import { useTranslation } from "react-i18next";

interface SmartVideoPlayerGalleryProps {
  videoUrl: string;
  style?: CSSProperties;
  muted?: boolean;
  /** Prefer false in feed, true in overlay. */
  showControls?: boolean;
  /** Enable click-to-toggle when native controls are hidden. */
  clickToToggle?: boolean;
  onMutedChange?: (muted: boolean) => void; // NEW
  preload?: 'none' | 'metadata' | 'auto';
}

function inferMime(url: string): string | undefined {
  const q = url.split('?')[0].toLowerCase();
  if (q.endsWith('.mp4')) return 'video/mp4';
  if (q.endsWith('.webm')) return 'video/webm';
  if (q.endsWith('.mov')) return 'video/quicktime';
  if (q.endsWith('.m4v')) return 'video/x-m4v';
  return undefined;
}

const SmartVideoPlayerGallery: React.FC<SmartVideoPlayerGalleryProps> = ({
  videoUrl,
  style,
  muted = true,
  showControls = true,   // overlay-friendly default
  clickToToggle = true,  // feed-friendly default
  onMutedChange,
  preload = 'metadata',
}) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wasMutedRef = useRef<boolean>(!!muted); // NEW

  const [isVisible, setIsVisible] = useState(false);
  const [userPaused, setUserPaused] = useState(false);

  // Hover/tap-driven controls visibility
  const [isHover, setIsHover] = useState(false);
  const hoverHideTimer = useRef<number | null>(null);

  const type = useMemo(() => inferMime(videoUrl), [videoUrl]);

  // Derived: only show native controls when hovered OR playing
const controlsActive = showControls && isHover;

  // Preload
  const effectivePreload = isVisible
    ? (preload ?? 'auto')
    : (preload ?? 'metadata');
  // ——— Visibility observer (preload ahead) ———
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { root: null, rootMargin: '800px 0px', threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.unobserve(el);
  }, []);

  // ——— Pause on tab hidden; resume only if not user-paused ———
  useEffect(() => {
    const onVis = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.hidden) {
        v.pause();
      } else if (isVisible && !userPaused) {
        v.play().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [isVisible, userPaused]);

  // ——— Autoplay when visible; pause when not (respect userPaused) ———
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isVisible && !userPaused) {
  try { v.preload = effectivePreload; } catch {}
  v.play().catch(() => {});
} else {
  v.pause();
}
  }, [isVisible, videoUrl, userPaused, effectivePreload]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = !!muted;
    wasMutedRef.current = !!muted;
  }, [muted]);

  // ——— Reset per-source, cleanup buffer ———
  useEffect(() => {
  setUserPaused(false);
  const v = videoRef.current;
  if (v) {
    v.pause();
    v.currentTime = 0;
  }
}, [videoUrl]);

useEffect(() => {
  if (videoRef.current) {
    videoRef.current.muted = true; // ✅ force muted for autoplay
    wasMutedRef.current = true;
  }
}, []);

  // ——— Hover/touch handling for controls visibility ———
  const scheduleHide = (ms = 1200) => {
    if (hoverHideTimer.current) window.clearTimeout(hoverHideTimer.current);
    hoverHideTimer.current = window.setTimeout(() => setIsHover(false), ms);
  };
  useEffect(() => {
    return () => {
      if (hoverHideTimer.current) window.clearTimeout(hoverHideTimer.current);
    };
  }, []);

  // ——— Overlay click to toggle (only when native controls are hidden) ———
  const onOverlayClick = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setUserPaused(false)).catch(() => {});
    } else {
      v.pause();
      setUserPaused(true);
    }
  };

  // Style: only adjust for landscape; otherwise use your original sizing
 const videoStyle: CSSProperties = {
  width: '100%',
  height: 'auto',
  maxHeight: '100%',
  objectFit: 'contain',   // ← IMPORTANT (no crop)
  backgroundColor: '#000',
  borderRadius: 12,
};
  

  return (
   <div
  ref={containerRef}
  style={{
  position: 'relative',
  width: '100%',
  height: '100%',        // fill tile
  maxHeight: '100%',     // prevent shrink
  borderRadius: 12,
  backgroundColor: '#000',
  overflow: 'hidden',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  ...style,
}}
      onMouseEnter={() => {
        if (hoverHideTimer.current) window.clearTimeout(hoverHideTimer.current);
        setIsHover(true);
      }}
      onMouseLeave={() => {
        // Hide controls after a short delay if paused; hide immediately if playing
        if (userPaused) scheduleHide(400);
        else setIsHover(false);
      }}
      onTouchStart={() => {
        // Mobile: brief reveal of controls
        setIsHover(true);
        scheduleHide(2000);
      }}
    >
     <video
  key={videoUrl}
  ref={videoRef}
  muted={muted}
  playsInline
  controls={controlsActive}
  preload={effectivePreload}
  controlsList="nodownload"
  style={{
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    backgroundColor: '#000',
    borderRadius: 12,
  }}
>

        <source src={videoUrl} {...(type ? { type } : {})} />
        {t("smartVideo.html5Fallback")}
      </video>

      {/* Click overlay: only when we want click-to-toggle and native controls are NOT visible */}
      {clickToToggle && !controlsActive && (
  <div
    onPointerDown={(e) => {
      // allow swipe gestures to bubble to parent
      if (Math.abs(e.movementX || 0) < 8) {
        onOverlayClick();
      }
    }}
    style={{
      position: 'absolute',
      inset: 0,
      background: 'transparent',
      cursor: 'pointer',
      touchAction: 'pan-x', // allow horizontal swipe
    }}
  />
)}

    </div>
  );
};

export default memo(SmartVideoPlayerGallery);
